import React, { useContext, useEffect, useState } from 'react';
import {
  Table, Button, Select, Modal, Tag, Space, message, Card,
  Typography, Row, Col, Statistic, Alert, Checkbox, Dropdown
} from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  TruckOutlined,
  CheckSquareOutlined,
  MoreOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  FilePdfOutlined,
  DownloadOutlined,
  FileExcelOutlined
} from '@ant-design/icons';
import { jwtDecode } from 'jwt-decode';
import store from 'store';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

import { useFetch } from '../../hooks';
import { LayoutContext } from '../../contexts';
import { URLS } from '../../constants';
import { getImageFallbackUrl, getPrimaryImageUrl } from '../../utils/helpers';

const { Title } = Typography;
const { Option } = Select;
const { confirm } = Modal;

const statusColors = {
  pending: 'orange',
  confirmed: 'blue',
  processing: 'purple',
  shipped: 'cyan',
  delivered: 'green',
  cancelled: 'red',
  rejected: 'red'
};

const statusIcons = {
  pending: <ClockCircleOutlined />,
  confirmed: <CheckCircleOutlined />,
  processing: <ReloadOutlined spin />,
  shipped: <TruckOutlined />,
  delivered: <CheckSquareOutlined />,
  cancelled: <ExclamationCircleOutlined />,
  rejected: <CloseCircleOutlined />
};

function Orders({ history }) {
  const { setHeaderComponent } = useContext(LayoutContext);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [userSettings, setUserSettings] = useState(null);
  const [businessApiError, setBusinessApiError] = useState(null);
  const [showOrderDetailsModal, setShowOrderDetailsModal] = useState(false);
  const [selectedOrderForDetails, setSelectedOrderForDetails] = useState(null);

  const { doFetch: fetchOrders } = useFetch();
  const { doFetch: updateOrderStatus } = useFetch();
  const { doFetch: bulkUpdateStatus } = useFetch();
  const { doFetch: fetchUserSettings } = useFetch();

  const getUserIdFromToken = () => {
    try {
      const token = store.get('authenticationToken');
      if (token) {
        const decoded = jwtDecode(token);
        return decoded.id;
      }
    } catch (error) {
      console.error('Failed to decode JWT token:', error);
    }
    return null;
  };

  const loadUserSettings = () => {
    fetchUserSettings({
      url: URLS.userSettings,
      onSuccess: (data) => {
        setUserSettings(data);
        if (data.business_api_url) {
          loadOrders(undefined, 1, 20, data); // Pass the data directly
        } else {
          setLoading(false);
          setBusinessApiError('Business API URL is not configured. Please set it in your settings.');
        }
      },
      onError: () => {
        setLoading(false);
        setBusinessApiError('Failed to load user settings.');
      },
    });
  };

  const loadOrders = (status, page = pagination.current, pageSize = pagination.pageSize, userSettingsData = null) => {
    const currentUserSettings = userSettingsData || userSettings;

    if (!currentUserSettings?.business_api_url) {
      return;
    }

    setLoading(true);
    setBusinessApiError(null);

    const userId = getUserIdFromToken();
    if (!userId) {
      setBusinessApiError('Invalid authentication token.');
      setLoading(false);
      return;
    }

    // Use selectedStatus if status is undefined, otherwise use the passed status
    const filterStatus = status !== undefined ? status : selectedStatus;

    console.log('=== LOAD ORDERS ===');
    console.log('Input status:', status);
    console.log('selectedStatus:', selectedStatus);
    console.log('filterStatus:', filterStatus);
    console.log('page:', page, 'pageSize:', pageSize);

    const params = {
      limit: pageSize,
      offset: (page - 1) * pageSize,
      sort_by: 'created_at',
      sort_direction: 'desc'
    };

    if (filterStatus) {
      params.status = filterStatus;
      console.log('✅ Sending status filter:', filterStatus);
      console.log('API params:', params);
    } else {
      console.log('❌ No status filter - showing all orders');
      console.log('API params:', params);
    }

    const apiUrl = `${currentUserSettings.business_api_url}/orders/dashboard/by-status`;
    fetchOrders({
      url: apiUrl,
      method: 'GET',
      params,
      headers: {
        'X-User-ID': userId.toString()
      },
      onSuccess: (data) => {
        console.log('=== API RESPONSE RECEIVED ===');
        console.log('Filter status sent:', filterStatus);
        console.log('Received orders count:', data.data?.length || 0);
        console.log('Total from API:', data.pagination?.total || 0);
        if (data.data && data.data.length > 0) {
          console.log('Sample orders:');
          data.data.slice(0, 3).forEach((order, index) => {
            console.log(`  Order ${index + 1}: ID=${order.id}, Status=${order.status}, Number=${order.order_number}`);
          });

          const statusCounts = data.data.reduce((acc, order) => {
            acc[order.status] = (acc[order.status] || 0) + 1;
            return acc;
          }, {});
          console.log('Status breakdown in response:', statusCounts);

          // Check if filtering worked
          const expectedStatus = filterStatus;
          const actualStatuses = Object.keys(statusCounts);
          if (expectedStatus && !actualStatuses.includes(expectedStatus)) {
            console.warn(`❌ FILTERING FAILED: Expected status "${expectedStatus}" but got statuses: ${actualStatuses.join(', ')}`);
          } else if (expectedStatus) {
            console.log(`✅ FILTERING SUCCESS: All orders have status "${expectedStatus}"`);
          } else {
            console.log(`ℹ️  NO FILTER: Showing all statuses: ${actualStatuses.join(', ')}`);
          }
        } else {
          console.log('No orders received');
        }

        const newOrders = data.data || [];
        console.log('=== SETTING ORDERS IN STATE ===');
        console.log('Orders being set in state:', newOrders.length);

        setOrders(newOrders);
        setPagination(prev => ({
          ...prev,
          current: page,
          pageSize,
          total: data.pagination?.total || 0
        }));
        setLoading(false);

        // Debug current state after update
        setTimeout(() => {
          console.log('=== STATE AFTER UPDATE ===');
          console.log('Current selectedStatus:', selectedStatus);
          console.log('Current orders in state:', orders.length);
        }, 100);
      },
      onError: (error) => {
        setBusinessApiError(error.message || 'Failed to fetch orders from business API.');
        setOrders([]);
        setLoading(false);
      },
    });
  };

  const handleStatusChange = (orderId, newStatus) => {
    confirm({
      title: `Change Order Status`,
      content: `Are you sure you want to change the status of order #${orderId} to "${newStatus}"?`,
      onOk() {
        updateSingleOrderStatus(orderId, newStatus);
      },
    });
  };

  const updateSingleOrderStatus = (orderId, newStatus) => {
    if (!userSettings?.business_api_url) return;

    const userId = getUserIdFromToken();
    if (!userId) {
      message.error('Invalid authentication token.');
      return;
    }

    updateOrderStatus({
      url: `${userSettings.business_api_url}/orders/${orderId}/status`,
      method: 'PATCH',
      params: { status: newStatus },
      headers: {
        'X-User-ID': userId.toString()
      },
      onSuccess: () => {
        message.success('Order status updated successfully');
        loadOrders();
      },
      onError: (error) => {
        message.error(error.message || 'Failed to update order status');
      },
    });
  };

  const handleBulkStatusChange = (newStatus) => {
    if (selectedOrders.length === 0) {
      message.warning('Please select orders to update');
      return;
    }

    confirm({
      title: `Bulk Update Order Status`,
      content: `Are you sure you want to change the status of ${selectedOrders.length} order(s) to "${newStatus}"?`,
      onOk() {
        performBulkUpdate(newStatus);
      },
    });
  };

  const performBulkUpdate = (newStatus) => {
    if (!userSettings?.business_api_url) return;

    const userId = getUserIdFromToken();
    if (!userId) {
      message.error('Invalid authentication token.');
      return;
    }

    bulkUpdateStatus({
      url: `${userSettings.business_api_url}/orders/dashboard/bulk-update-status`,
      method: 'PUT',
      params: {
        order_ids: selectedOrders,
        status: newStatus
      },
      headers: {
        'X-User-ID': userId.toString()
      },
      onSuccess: (data) => {
        const { updated_orders, skipped_orders, errors } = data.data;
        message.success(`${updated_orders.length} orders updated successfully`);
        if (skipped_orders.length > 0) {
          message.info(`${skipped_orders.length} orders were already in the target status`);
        }
        if (errors.length > 0) {
          message.warning(`${errors.length} orders failed to update`);
        }
        setSelectedOrders([]);
        loadOrders();
      },
      onError: (error) => {
        message.error(error.message || 'Failed to bulk update orders');
      },
    });
  };

  const handleStatusFilterChange = (status) => {
    console.log('=== STATUS FILTER CHANGE ===');
    console.log('Selected status:', status);
    console.log('Current selectedStatus:', selectedStatus);
    setSelectedStatus(status);
    console.log('About to call loadOrders with status:', status);
    // Pass the status directly, don't rely on default parameter
    loadOrders(status, 1);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    loadOrders(selectedStatus, pagination.current, pagination.pageSize);
  };

  const handleSelectOrder = (orderId, checked) => {
    if (checked) {
      setSelectedOrders(prev => [...prev, orderId]);
    } else {
      setSelectedOrders(prev => prev.filter(id => id !== orderId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedOrders(orders.map(order => order.id));
    } else {
      setSelectedOrders([]);
    }
  };

  const viewOrderDetails = (order) => {
    setSelectedOrderForDetails(order);
    setShowOrderDetailsModal(true);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US').format(price || 0);
  };

  const getCartItems = (cartItemsJson) => {
    try {
      if (typeof cartItemsJson === 'string') {
        return JSON.parse(cartItemsJson) || [];
      }
      return cartItemsJson || [];
    } catch {
      return [];
    }
  };

  const getProductName = (item) => {
    // Prefer Arabic (index 1), fallback to English (index 0)
    return (
      item.product_translations?.[1]?.name_text ||
      item.product_translations?.[0]?.name_text ||
      item.name ||
      'Unknown Product'
    );
  };

  const expandCartItems = (cartItemsJson) => {
    const items = getCartItems(cartItemsJson);
    const expandedItems = [];

    items.forEach((item) => {
      const applyDiscount = (price, discount) => {
        const d = Number(discount || 0);
        return d > 0 ? price * (1 - d / 100) : price;
      };
      const count = typeof item.count === 'number' ? item.count : 1;

      if (!item.variantId) {
        // Simple product without variants
        const basePrice = Number(item.price_wholesale || item.price || 0);
        const finalPrice = applyDiscount(basePrice, item.discount);

        expandedItems.push({
          ...item,
          variantType: 'simple',
          variantName: '',
          variantPrice: finalPrice,
          count,
          totalPrice: finalPrice * count,
        });
      } else if (
        item.variantId.includes('_size_') &&
        item.variantId.includes('color_')
      ) {
        // Combined color + size variant
        const colorStart = item.variantId.indexOf('color_') + 6;
        const sizeDelimiter = item.variantId.indexOf('_size_');
        const colorImageLink = item.variantId.substring(colorStart, sizeDelimiter);
        const sizeNameFromId = item.variantId.split('_size_')[1];

        let variantName = '';
        let variantColor = null;

        const colorIndex =
          item.colors?.findIndex((c) => c.imageLink === colorImageLink) ?? -1;
        if (colorIndex !== -1 && item.colors) {
          const color = item.colors[colorIndex];
          const colorName =
            color['colorName.2'] ||
            color['colorName.1'] ||
            color.name ||
            `Color ${colorIndex + 1}`;
          variantColor = color.hexcode;

          const sizeIndex =
            item.sizes?.findIndex(
              (s) =>
                s['sizeName.2'] === sizeNameFromId ||
                s['sizeName.1'] === sizeNameFromId ||
                s['sizeName.3'] === sizeNameFromId
            ) ?? -1;

          if (sizeIndex !== -1 && item.sizes) {
            const size = item.sizes[sizeIndex];
            const actualSizeName =
              size['sizeName.2'] ||
              size['sizeName.1'] ||
              `Size ${sizeIndex + 1}`;
            variantName = `${colorName} - ${actualSizeName}`;
          } else {
            variantName = colorName;
          }
        }

        const basePrice = Number(item.price_wholesale || item.price || 0);
        const finalPrice = applyDiscount(basePrice, item.discount);
        expandedItems.push({
          ...item,
          variantType: 'color_size',
          variantName,
          variantColor,
          variantPrice: finalPrice,
          count,
          totalPrice: finalPrice * count,
        });
      } else if (item.variantId.startsWith('color_')) {
        const colorImageLink = item.variantId.replace('color_', '');
        const colorIndex =
          item.colors?.findIndex((c) => c.imageLink === colorImageLink) ?? -1;
        let variantName = '';
        let variantColor = null;

        if (colorIndex !== -1 && item.colors) {
          const color = item.colors[colorIndex];
          variantName =
            color['colorName.2'] ||
            color['colorName.1'] ||
            color.name ||
            `Color ${colorIndex + 1}`;
          variantColor = color.hexcode;
        }

        const basePrice = Number(item.price_wholesale || item.price || 0);
        const finalPrice = applyDiscount(basePrice, item.discount);
        expandedItems.push({
          ...item,
          variantType: 'color',
          variantName,
          variantColor,
          variantPrice: finalPrice,
          count,
          totalPrice: finalPrice * count,
        });
      } else if (item.variantId.startsWith('size_')) {
        const sizeNameFromId = item.variantId.replace('size_', '');
        const sizeIndex =
          item.sizes?.findIndex(
            (s) =>
              s['sizeName.2'] === sizeNameFromId ||
              s['sizeName.1'] === sizeNameFromId ||
              s['sizeName.3'] === sizeNameFromId
          ) ?? -1;

        let variantName = sizeNameFromId;
        let basePrice = Number(item.price_wholesale || item.price || 0);
        let sizeDiscount = item.discount;

        if (sizeIndex !== -1 && item.sizes) {
          const size = item.sizes[sizeIndex];
          variantName =
            size['sizeName.2'] || size['sizeName.1'] || sizeNameFromId;
          basePrice = Number(size.price || item.price_wholesale || item.price || 0);
          sizeDiscount = size.discount || item.discount;
        }

        const finalPrice = applyDiscount(basePrice, sizeDiscount);
        expandedItems.push({
          ...item,
          variantType: 'size',
          variantName,
          variantPrice: finalPrice,
          count,
          totalPrice: finalPrice * count,
        });
      } else {
        // Unknown variantId format — display as-is
        const basePrice = Number(item.price_wholesale || item.price || 0);
        const finalPrice = applyDiscount(basePrice, item.discount);
        expandedItems.push({
          ...item,
          variantType: 'simple',
          variantName: '',
          variantPrice: finalPrice,
          count,
          totalPrice: finalPrice * count,
        });
      }
    });

    return expandedItems;
  };

  const exportOrderToPDF = async (order) => {
    try {
      // Get the modal content element
      const modalContent = document.getElementById('order-details-content');

      if (!modalContent) {
        message.error('Could not find order details to export');
        return;
      }

      // Show loading message
      const hideLoading = message.loading('Generating PDF...', 0);

      // Store original styles to restore later
      const originalMaxHeight = modalContent.style.maxHeight;
      const originalOverflowY = modalContent.style.overflowY;
      const originalHeight = modalContent.style.height;

      // Temporarily remove height restrictions to capture full content
      modalContent.style.maxHeight = 'none';
      modalContent.style.overflowY = 'visible';
      modalContent.style.height = 'auto';

      // Wait for all images to load
      const images = modalContent.querySelectorAll('img');
      await Promise.all(
        Array.from(images).map(img => {
          if (img.complete) return Promise.resolve();
          return new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = resolve; // Resolve even on error to not block the process
            // Set a timeout to prevent hanging
            setTimeout(resolve, 3000);
          });
        })
      );

      // Small delay to ensure DOM is fully rendered with new height
      await new Promise(resolve => setTimeout(resolve, 500));

      // Use html2canvas to capture the modal content
      const canvas = await html2canvas(modalContent, {
        scale: 2, // Higher quality
        useCORS: true, // Enable CORS for images
        allowTaint: true, // Allow tainted canvas to capture images
        logging: false,
        backgroundColor: '#ffffff',
        imageTimeout: 15000, // Longer timeout for loading images
        foreignObjectRendering: false, // Better compatibility
        windowHeight: modalContent.scrollHeight, // Capture full height
        height: modalContent.scrollHeight, // Set capture height to full content height
        onclone: (clonedDoc) => {
          // Add padding to the cloned document
          const clonedContent = clonedDoc.getElementById('order-details-content');
          if (clonedContent) {
            clonedContent.style.padding = '20px';
            clonedContent.style.backgroundColor = '#ffffff';
            clonedContent.style.maxHeight = 'none';
            clonedContent.style.overflowY = 'visible';
            clonedContent.style.height = 'auto';

            // Ensure all fonts are loaded and properly set
            clonedContent.style.fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif';

            // Force image loading
            const imgs = clonedContent.querySelectorAll('img');
            imgs.forEach(img => {
              img.style.maxWidth = '100%';
              img.style.height = 'auto';
            });
          }
        }
      });

      // Restore original styles
      modalContent.style.maxHeight = originalMaxHeight;
      modalContent.style.overflowY = originalOverflowY;
      modalContent.style.height = originalHeight;

      // Calculate PDF dimensions with padding
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Create PDF
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Add the canvas image to PDF with margins
      const margin = 10; // 10mm margin
      const contentWidth = imgWidth - (2 * margin);
      const contentHeight = (canvas.height * contentWidth) / canvas.width;

      let heightLeft = contentHeight;
      let position = margin;

      // Add first page
      pdf.addImage(canvas.toDataURL('image/png', 1.0), 'PNG', margin, position, contentWidth, contentHeight);
      heightLeft -= (pageHeight - 2 * margin);

      // Add additional pages if content is longer than one page
      while (heightLeft > 0) {
        position = heightLeft - contentHeight + margin;
        pdf.addPage();
        pdf.addImage(canvas.toDataURL('image/png', 1.0), 'PNG', margin, position, contentWidth, contentHeight);
        heightLeft -= (pageHeight - 2 * margin);
      }

      // Save the PDF
      pdf.save(`Order-${order.id}.pdf`);

      hideLoading();
      message.success('PDF exported successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      message.error('Failed to generate PDF');
    }
  };

  const exportBulkOrdersToPDF = async () => {
    if (selectedOrders.length === 0) {
      message.warning('Please select orders to export');
      return;
    }

    try {
      const ordersToExport = orders.filter(order => selectedOrders.includes(order.id));
      const hideLoading = message.loading(`Exporting ${ordersToExport.length} orders...`, 0);

      // Export each order by temporarily showing its details and capturing
      for (let i = 0; i < ordersToExport.length; i++) {
        const order = ordersToExport[i];

        // Set the order for details view
        setSelectedOrderForDetails(order);
        setShowOrderDetailsModal(true);

        // Wait for modal to render
        await new Promise(resolve => setTimeout(resolve, 500));

        // Get the modal content
        const modalContent = document.getElementById('order-details-content');

        if (modalContent) {
          // Store original styles
          const originalMaxHeight = modalContent.style.maxHeight;
          const originalOverflowY = modalContent.style.overflowY;
          const originalHeight = modalContent.style.height;

          // Remove height restrictions
          modalContent.style.maxHeight = 'none';
          modalContent.style.overflowY = 'visible';
          modalContent.style.height = 'auto';

          // Wait for all images to load
          const images = modalContent.querySelectorAll('img');
          await Promise.all(
            Array.from(images).map(img => {
              if (img.complete) return Promise.resolve();
              return new Promise((resolve) => {
                img.onload = resolve;
                img.onerror = resolve;
                setTimeout(resolve, 3000);
              });
            })
          );

          // Small delay for rendering with new height
          await new Promise(resolve => setTimeout(resolve, 500));

          // Capture the modal
          const canvas = await html2canvas(modalContent, {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            logging: false,
            backgroundColor: '#ffffff',
            imageTimeout: 15000,
            foreignObjectRendering: false,
            windowHeight: modalContent.scrollHeight,
            height: modalContent.scrollHeight,
            onclone: (clonedDoc) => {
              const clonedContent = clonedDoc.getElementById('order-details-content');
              if (clonedContent) {
                clonedContent.style.padding = '20px';
                clonedContent.style.backgroundColor = '#ffffff';
                clonedContent.style.maxHeight = 'none';
                clonedContent.style.overflowY = 'visible';
                clonedContent.style.height = 'auto';
                clonedContent.style.fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif';

                const imgs = clonedContent.querySelectorAll('img');
                imgs.forEach(img => {
                  img.style.maxWidth = '100%';
                  img.style.height = 'auto';
                });
              }
            }
          });

          // Restore original styles
          modalContent.style.maxHeight = originalMaxHeight;
          modalContent.style.overflowY = originalOverflowY;
          modalContent.style.height = originalHeight;

          // Calculate PDF dimensions with padding
          const imgWidth = 210;
          const pageHeight = 297;
          const margin = 10;
          const contentWidth = imgWidth - (2 * margin);
          const contentHeight = (canvas.height * contentWidth) / canvas.width;

          // Create PDF
          const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4'
          });

          let heightLeft = contentHeight;
          let position = margin;

          // Add first page
          pdf.addImage(canvas.toDataURL('image/png', 1.0), 'PNG', margin, position, contentWidth, contentHeight);
          heightLeft -= (pageHeight - 2 * margin);

          // Add additional pages if needed
          while (heightLeft > 0) {
            position = heightLeft - contentHeight + margin;
            pdf.addPage();
            pdf.addImage(canvas.toDataURL('image/png', 1.0), 'PNG', margin, position, contentWidth, contentHeight);
            heightLeft -= (pageHeight - 2 * margin);
          }

          // Save
          pdf.save(`Order-${order.id}.pdf`);

          // Wait a bit before next export
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      }

      // Close modal
      setShowOrderDetailsModal(false);
      setSelectedOrderForDetails(null);

      hideLoading();
      message.success(`${ordersToExport.length} orders exported successfully!`);
    } catch (error) {
      console.error('Error generating bulk PDF:', error);
      message.error('Failed to generate PDF');
    }
  };

  const exportOrderToCSV = (order) => {
    try {
      const expandedItems = expandCartItems(order.cart_items_json);

      // CSV Headers
      const headers = [
        'Order ID',
        'Order Date',
        'Status',
        'Customer Name',
        'Phone',
        'City',
        'Address',
        'Shipping Method',
        'Shipping Duration',
        'Shipping Cost',
        'Product Name',
        'Variant',
        'Quantity',
        'Unit Price',
        'Total Price',
        'Cart Total',
        'Delivery Fees',
        'Discount',
        'Grand Total',
        'Payment Method',
        'Coupon Code',
        'Notes'
      ];

      // Build CSV rows
      const rows = [];

      // Add header row
      rows.push(headers.join(','));

      // Add data rows (one row per product/variant)
      expandedItems.forEach((item, index) => {
        const row = [
          order.id,
          `"${new Date(order.created_at).toLocaleString()}"`, // Wrap date in quotes
          order.status,
          `"${order.fullname}"`,
          order.phone_number,
          `"${order.city}"`,
          `"${order.address}"`,
          `"${order.shipping_option_name}"`,
          order.shipping_duration || '',
          order.shipping_price,
          `"${getProductName(item)}"`,
          item.variantType !== 'simple' ? `"${item.variantName}"` : '-',
          item.count,
          item.variantPrice,
          item.totalPrice,
          index === 0 ? order.cart_total : '', // Only show on first row
          index === 0 ? order.delivery_fees : '',
          index === 0 ? (order.discount_amount || 0) : '',
          index === 0 ? order.grand_total : '',
          index === 0 ? order.payment_method : '',
          index === 0 ? (order.coupon_code || '') : '',
          index === 0 ? `"${(order.notes || '').replace(/"/g, '""')}"` : ''
        ];
        rows.push(row.join(','));
      });

      // Create CSV content
      const csvContent = rows.join('\n');

      // Create blob and download
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', `Order-${order.id}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success('CSV exported successfully!');
    } catch (error) {
      console.error('Error generating CSV:', error);
      message.error('Failed to generate CSV');
    }
  };

  const exportBulkOrdersToCSV = () => {
    if (selectedOrders.length === 0) {
      message.warning('Please select orders to export');
      return;
    }

    try {
      const ordersToExport = orders.filter(order => selectedOrders.includes(order.id));

      // CSV Headers
      const headers = [
        'Order ID',
        'Order Date',
        'Status',
        'Customer Name',
        'Phone',
        'City',
        'Address',
        'Shipping Method',
        'Shipping Duration',
        'Shipping Cost',
        'Product Name',
        'Variant',
        'Quantity',
        'Unit Price',
        'Total Price',
        'Cart Total',
        'Delivery Fees',
        'Discount',
        'Grand Total',
        'Payment Method',
        'Coupon Code',
        'Notes'
      ];

      // Build CSV rows
      const rows = [];

      // Add header row
      rows.push(headers.join(','));

      // Add data rows for all orders
      ordersToExport.forEach(order => {
        const expandedItems = expandCartItems(order.cart_items_json);

        expandedItems.forEach((item, index) => {
          const row = [
            order.id,
            `"${new Date(order.created_at).toLocaleString()}"`, // Wrap date in quotes
            order.status,
            `"${order.fullname}"`,
            order.phone_number,
            `"${order.city}"`,
            `"${order.address}"`,
            `"${order.shipping_option_name}"`,
            order.shipping_duration || '',
            order.shipping_price,
            `"${getProductName(item)}"`,
            item.variantType !== 'simple' ? `"${item.variantName}"` : '-',
            item.count,
            item.variantPrice,
            item.totalPrice,
            index === 0 ? order.cart_total : '', // Only show on first row of each order
            index === 0 ? order.delivery_fees : '',
            index === 0 ? (order.discount_amount || 0) : '',
            index === 0 ? order.grand_total : '',
            index === 0 ? order.payment_method : '',
            index === 0 ? (order.coupon_code || '') : '',
            index === 0 ? `"${(order.notes || '').replace(/"/g, '""')}"` : ''
          ];
          rows.push(row.join(','));
        });
      });

      // Create CSV content
      const csvContent = rows.join('\n');

      // Create blob and download
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', `Orders-Bulk-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success(`${ordersToExport.length} orders exported to CSV successfully!`);
    } catch (error) {
      console.error('Error generating bulk CSV:', error);
      message.error('Failed to generate CSV');
    }
  };

  useEffect(() => {
    loadUserSettings();

    // Clear any existing header component
    setHeaderComponent(null);

    return () => {
      setHeaderComponent(null);
    };
  }, []);

  // Status change handling is done in handleStatusFilterChange
  // This useEffect is removed to avoid duplicate API calls

  // Auto-refresh effect - only start when userSettings is loaded
  // COMMENTED OUT: Polling functionality disabled
  /*
  useEffect(() => {
    if (!userSettings?.business_api_url) {
      return;
    }

    const interval = setInterval(() => {
      loadOrders(selectedStatus, pagination.current, pagination.pageSize);
    }, 60000); // 60 seconds

    return () => {
      clearInterval(interval);
    };
  }, [userSettings?.business_api_url, selectedStatus, pagination.current, pagination.pageSize]);
  */

  const columns = [
    {
      title: (
        <Checkbox
          checked={selectedOrders.length === orders.length && orders.length > 0}
          indeterminate={selectedOrders.length > 0 && selectedOrders.length < orders.length}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      key: 'select',
      width: 50,
      render: (record) => (
        <Checkbox
          checked={selectedOrders.includes(record.id)}
          onChange={(e) => handleSelectOrder(record.id, e.target.checked)}
        />
      ),
    },
    {
      title: 'Order #',
      dataIndex: 'id',
      key: 'id',
      render: (orderNumber) => <strong>{orderNumber}</strong>,
    },
    {
      title: 'Customer',
      dataIndex: 'fullname',
      key: 'fullname',
      render: (name, record) => (
        <div>
          <div><strong>{name}</strong></div>
          <div style={{ fontSize: '12px', color: '#666' }}>{record.phone_number}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.address && `${record.address}`}
            {record.address && record.city && ', '}
            {record.city && `${record.city}`}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.shipping_option_name && `Governorate: ${record.shipping_option_name}`}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.shipping_duration && `Duration: ${record.shipping_duration}`}
          </div>
          <div style={{ marginTop: '4px' }}>
            <Tag color={statusColors[record.status]} icon={statusIcons[record.status]} style={{ fontSize: '11px' }}>
              {record.status.toUpperCase()}
            </Tag>
          </div>
        </div>
      ),
    },
    // Commented out: Status column moved to Customer column
    /*
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={statusColors[status]} icon={statusIcons[status]}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    */
    {
      title: 'Total',
      dataIndex: 'grand_total',
      key: 'grand_total',
      render: (total, record) => {
        const hasCoupon = record.coupon_code || record.discount_amount || record.coupon_applied;
        return (
          <div>
            <div><strong>{total} {record.currency}</strong></div>
            {hasCoupon && (
              <div style={{ fontSize: '11px', marginTop: '2px', color: '#666' }}>
                <Tag color="green" style={{ fontSize: '10px' }}>Coupon Applied</Tag>
                {record.coupon_code && (
                  <div style={{ marginTop: '1px' }}>
                    Code: {record.coupon_code}
                  </div>
                )}
                {record.discount_amount && (
                  <div style={{ color: '#52c41a' }}>
                    Saved: {record.discount_amount} {record.currency}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      },
    },
    // Commented out: Coupon column moved to Total column
    /*
    {
      title: 'Coupon',
      key: 'coupon',
      render: (record) => {
        const hasCoupon = record.coupon_code || record.discount_amount || record.coupon_applied;
        return hasCoupon ? (
          <div>
            <Tag color="green">Applied</Tag>
            {record.coupon_code && (
              <div style={{ fontSize: '12px', marginTop: '2px' }}>
                Code: {record.coupon_code}
              </div>
            )}
            {record.discount_amount && (
              <div style={{ fontSize: '12px', color: '#666' }}>
                Discount: {record.discount_amount} {record.currency}
              </div>
            )}
          </div>
        ) : (
          <Tag color="default">None</Tag>
        );
      },
    },
    */
    // Commented out: Date column hidden
    /*
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleDateString(),
    },
    */
    {
      title: 'View Details',
      key: 'view_details',
      width: 120,
      render: (record) => (
        <Button
          type="primary"
          onClick={() => viewOrderDetails(record)}
        >
          View Details
        </Button>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'confirmed',
                label: 'Mark as Confirmed',
                onClick: () => handleStatusChange(record.id, 'confirmed'),
                disabled: record.status === 'confirmed' || record.status === 'cancelled'
              },
              {
                key: 'processing',
                label: 'Mark as Processing',
                onClick: () => handleStatusChange(record.id, 'processing'),
                disabled: record.status === 'processing' || record.status === 'cancelled'
              },
              {
                key: 'shipped',
                label: 'Mark as Shipped',
                onClick: () => handleStatusChange(record.id, 'shipped'),
                disabled: record.status === 'shipped' || record.status === 'cancelled'
              },
              {
                key: 'delivered',
                label: 'Mark as Delivered',
                onClick: () => handleStatusChange(record.id, 'delivered'),
                disabled: record.status === 'delivered' || record.status === 'cancelled'
              },
              // Commented out: Mark as Cancelled option hidden
              /*
              {
                key: 'cancelled',
                label: 'Mark as Cancelled',
                onClick: () => handleStatusChange(record.id, 'cancelled'),
                disabled: record.status === 'cancelled'
              },
              */
              {
                key: 'rejected',
                label: 'Mark as Rejected',
                onClick: () => handleStatusChange(record.id, 'rejected'),
                disabled: record.status === 'rejected' || record.status === 'cancelled'
              },
            ],
          }}
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  if (!userSettings?.business_api_url) {
    return (
      <div style={{ padding: '24px' }}>
        <Alert
          message="Business API Not Configured"
          description={
            <>
              You need to configure your business API URL in the{' '}
              <a href="/settings">settings page</a> before you can manage orders.
            </>
          }
          type="warning"
          showIcon
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Title level={2} style={{ marginBottom: '24px' }}>
          Order Management
        </Title>

        {businessApiError && (
          <Alert
            message="Connection Error"
            description={businessApiError}
            type="error"
            showIcon
            style={{ marginBottom: '24px' }}
            closable
          />
        )}

        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Orders"
                value={pagination.total}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Selected Orders"
                value={selectedOrders.length}
                prefix={<CheckSquareOutlined />}
              />
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Filter by Status"
              allowClear
              style={{ width: '100%' }}
              value={selectedStatus}
              onChange={handleStatusFilterChange}
            >
              <Option value="">All</Option>
              <Option value="pending">Pending</Option>
              <Option value="confirmed">Confirmed</Option>
              <Option value="processing">Processing</Option>
              <Option value="shipped">Shipped</Option>
              <Option value="delivered">Delivered</Option>
              <Option value="cancelled">Cancelled</Option>
              <Option value="rejected">Rejected</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={18}>
            <Space wrap>
              <Button
                type="primary"
                onClick={() => handleBulkStatusChange('confirmed')}
                disabled={selectedOrders.length === 0}
              >
                Bulk Confirm ({selectedOrders.length})
              </Button>
              <Button
                type="primary"
                onClick={() => handleBulkStatusChange('processing')}
                disabled={selectedOrders.length === 0}
              >
                Bulk Process ({selectedOrders.length})
              </Button>
              <Button
                type="primary"
                onClick={() => handleBulkStatusChange('shipped')}
                disabled={selectedOrders.length === 0}
              >
                Bulk Ship ({selectedOrders.length})
              </Button>
              <Button
                danger
                onClick={() => handleBulkStatusChange('rejected')}
                disabled={selectedOrders.length === 0}
              >
                Bulk Reject ({selectedOrders.length})
              </Button>
              <Button
                onClick={exportBulkOrdersToPDF}
                icon={<FilePdfOutlined />}
                disabled={selectedOrders.length === 0}
              >
                Export PDF ({selectedOrders.length})
              </Button>
              <Button
                onClick={exportBulkOrdersToCSV}
                icon={<FileExcelOutlined />}
                disabled={selectedOrders.length === 0}
              >
                Export CSV ({selectedOrders.length})
              </Button>
              <Button
                onClick={() => loadOrders(selectedStatus || null, pagination.current, pagination.pageSize)}
                icon={<ReloadOutlined />}
                loading={loading}
              >
                Refresh
              </Button>
            </Space>
          </Col>
        </Row>

        {console.log('=== RENDERING TABLE ===')}
        {console.log('Orders in table:', orders.length)}
        {orders.length > 0 && console.log('First order status:', orders[0].status)}

        <Table
          columns={columns}
          dataSource={orders}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} orders`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1100 }}
        />
      </Card>

      {/* Order Details Modal */}
      <Modal
        title={
          selectedOrderForDetails
            ? `Order Details - #${selectedOrderForDetails.id}`
            : 'Order Details'
        }
        open={showOrderDetailsModal}
        onCancel={() => setShowOrderDetailsModal(false)}
        width={900}
        footer={[
          <Button
            key="export-pdf"
            type="primary"
            icon={<FilePdfOutlined />}
            onClick={() => exportOrderToPDF(selectedOrderForDetails)}
          >
            Export PDF
          </Button>,
          <Button
            key="export-csv"
            icon={<FileExcelOutlined />}
            onClick={() => exportOrderToCSV(selectedOrderForDetails)}
          >
            Export CSV
          </Button>,
          <Button key="close" onClick={() => setShowOrderDetailsModal(false)}>
            Close
          </Button>,
        ]}
      >
        {selectedOrderForDetails && (
          <div
            id="order-details-content"
            style={{
              maxHeight: '70vh',
              overflowY: 'auto',
              padding: '16px',
              backgroundColor: '#ffffff',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif'
            }}
          >
            {/* Order Header */}
            <div style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: '16px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <div>
                  <Title level={4} style={{ margin: 0 }}>
                    Order #{selectedOrderForDetails.id}
                  </Title>
                  <p style={{ color: '#666', marginTop: '4px' }}>
                    Ordered on {new Date(selectedOrderForDetails.created_at).toLocaleString()}
                  </p>
                </div>
                <Tag
                  color={statusColors[selectedOrderForDetails.status]}
                  icon={statusIcons[selectedOrderForDetails.status]}
                  style={{ fontSize: '14px', padding: '4px 12px' }}
                >
                  {selectedOrderForDetails.status.toUpperCase()}
                </Tag>
              </div>
            </div>

            {/* Order Items */}
            <div style={{ marginBottom: '24px' }}>
              <Title level={5}>Order Items</Title>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {expandCartItems(selectedOrderForDetails.cart_items_json).map(
                  (item, index) => (
                    <div
                      key={`${item.id}-${item.variantType}-${index}`}
                      style={{
                        display: 'flex',
                        gap: '16px',
                        padding: '12px',
                        backgroundColor: '#fafafa',
                        borderRadius: '8px',
                      }}
                    >
                      {/* Product Image */}
                      {item.image_url ? (
                        <img
                          src={getPrimaryImageUrl(item.image_url)}
                          alt={getProductName(item)}
                          style={{
                            width: '80px',
                            height: '80px',
                            objectFit: 'cover',
                            borderRadius: '8px',
                          }}
                          onError={(e) => {
                            console.log('Orders image failed:', e.target.src, 'Original URL:', item.image_url);
                            const fallbackUrl = getImageFallbackUrl(e.target.src);
                            console.log('Trying fallback:', fallbackUrl);
                            if (fallbackUrl !== e.target.src) {
                              e.target.src = fallbackUrl;
                            } else {
                              console.log('No fallback available, hiding image');
                              e.target.style.display = 'none';
                            }
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: '80px',
                            height: '80px',
                            backgroundColor: '#e0e0e0',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '32px',
                          }}
                        >
                          📦
                        </div>
                      )}

                      {/* Product Details */}
                      <div style={{ flex: 1 }}>
                        <h5 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '500' }}>
                          {getProductName(item)}
                        </h5>

                        {/* Variant Information */}
                        {item.variantType !== 'simple' && (
                          <div style={{ marginBottom: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              {/* Color Badge */}
                              {(item.variantType === 'color' || item.variantType === 'color_size') && item.variantColor && (
                                <div
                                  style={{
                                    width: '16px',
                                    height: '16px',
                                    borderRadius: '50%',
                                    border: '1px solid #d9d9d9',
                                    backgroundColor: item.variantColor,
                                  }}
                                />
                              )}
                              {/* Variant Label */}
                              <span style={{ fontSize: '14px', color: '#666' }}>
                                {item.variantName}
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Count and Price */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>
                            Quantity: {item.count}
                          </p>
                        </div>
                      </div>

                      {/* Price */}
                      <div style={{ textAlign: 'right' }}>
                        <p style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>
                          {formatPrice(item.totalPrice)} {selectedOrderForDetails.currency}
                        </p>
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>

            {/* Order Summary */}
            <div style={{ backgroundColor: '#fafafa', padding: '16px', borderRadius: '8px', marginBottom: '24px' }}>
              <Title level={5}>Order Summary</Title>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Subtotal</span>
                  <span>
                    {formatPrice(selectedOrderForDetails.cart_total)}{' '}
                    {selectedOrderForDetails.currency}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Delivery Fees</span>
                  <span>
                    {formatPrice(selectedOrderForDetails.delivery_fees)}{' '}
                    {selectedOrderForDetails.currency}
                  </span>
                </div>
                {selectedOrderForDetails.discount_amount > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#52c41a' }}>
                    <span>Discount</span>
                    <span>
                      -{formatPrice(selectedOrderForDetails.discount_amount)}{' '}
                      {selectedOrderForDetails.currency}
                    </span>
                  </div>
                )}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    borderTop: '1px solid #d9d9d9',
                    paddingTop: '8px',
                    fontWeight: '600',
                    fontSize: '16px',
                  }}
                >
                  <span>Total</span>
                  <span>
                    {formatPrice(selectedOrderForDetails.grand_total)}{' '}
                    {selectedOrderForDetails.currency}
                  </span>
                </div>
              </div>
            </div>

            {/* Shipping & Payment Info */}
            <Row gutter={24} style={{ marginBottom: '24px' }}>
              <Col span={12}>
                <Title level={5}>Shipping Information</Title>
                <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
                  <p style={{ margin: '4px 0' }}>
                    <strong>Address:</strong> {selectedOrderForDetails.address}
                  </p>
                  {selectedOrderForDetails.latitude && selectedOrderForDetails.longitude && (
                    <p style={{ margin: '4px 0' }}>
                      <strong>Delivery Location:</strong>{' '}
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${selectedOrderForDetails.latitude},${selectedOrderForDetails.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#1890ff', textDecoration: 'underline' }}
                      >
                        View on Google Maps
                      </a>
                    </p>
                  )}
                  {selectedOrderForDetails.delivery_address && (
                    <p style={{ margin: '4px 0' }}>
                      <strong>Detected Address:</strong> {selectedOrderForDetails.delivery_address}
                    </p>
                  )}
                  {selectedOrderForDetails.address_details && (
                    <p style={{ margin: '4px 0' }}>
                      <strong>Building/Apt/Floor:</strong> {selectedOrderForDetails.address_details}
                    </p>
                  )}
                  <p style={{ margin: '4px 0' }}>
                    <strong>City:</strong> {selectedOrderForDetails.city}
                  </p>
                  <p style={{ margin: '4px 0' }}>
                    <strong>Phone:</strong> {selectedOrderForDetails.phone_number}
                  </p>
                  <p style={{ margin: '4px 0' }}>
                    <strong>Shipping Method:</strong> {selectedOrderForDetails.shipping_option_name}
                  </p>
                  {selectedOrderForDetails.shipping_duration && (
                    <p style={{ margin: '4px 0' }}>
                      <strong>Duration:</strong> {selectedOrderForDetails.shipping_duration}
                    </p>
                  )}
                </div>
              </Col>
              <Col span={12}>
                <Title level={5}>Payment Information</Title>
                <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
                  <p style={{ margin: '4px 0' }}>
                    <strong>Payment Method:</strong>{' '}
                    {selectedOrderForDetails.payment_method.charAt(0).toUpperCase() +
                      selectedOrderForDetails.payment_method.slice(1)}
                  </p>
                  {selectedOrderForDetails.coupon_code && (
                    <p style={{ margin: '4px 0' }}>
                      <strong>Coupon Used:</strong> {selectedOrderForDetails.coupon_code}
                    </p>
                  )}
                </div>
              </Col>
            </Row>

            {/* Notes */}
            {selectedOrderForDetails.notes && (
              <div
                style={{
                  backgroundColor: '#e6f7ff',
                  padding: '16px',
                  borderRadius: '8px',
                  border: '1px solid #91d5ff',
                }}
              >
                <Title level={5} style={{ marginBottom: '8px' }}>
                  Customer Notes
                </Title>
                <p style={{ margin: 0, color: '#262626' }}>{selectedOrderForDetails.notes}</p>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

export default Orders;
